// The permissions the panel knows. What each one guards is decided where it is used: routes.tsx for
// views, the host slices for action flags. Who holds them is app-service's call.
export const PERMISSIONS = {
  'care.search': 'The search and every action on its results',
  'operations.amazon_door_access': 'The Amazon Access location gate: reads and batch writes',
  'operations.carrier_exclusion_lists': 'The carriers\' exclusion lists of registered «Usuarios»: list, add, remove',
  'access.manage': 'Assigning roles and per-person extra permissions (the access-management area)',
} as const;

export type Permission = keyof typeof PERMISSIONS;

// What the access screen calls each permission. Keyed by `Permission`, so a permission added to the
// catalog without its label fails typecheck instead of rendering a raw identifier.
// TODO: i18n
export const PERMISSION_LABELS: Record<Permission, string> = {
  'care.search': 'Búsqueda y todas sus acciones',
  'operations.amazon_door_access': 'Vista de Amazon Access',
  'operations.carrier_exclusion_lists': 'Listas de exclusión de carriers',
  'access.manage': 'Gestión de roles y permisos',
};

// The access-management area is never handed out from the panel: whoever holds it is managed from
// backend. Matched by area prefix, not by catalog, so an `access.*` identifier the panel has not
// learned yet is kept out too.
export function isAccessManagement(id: string): boolean {
  return id.startsWith('access.');
}

const KNOWN: ReadonlySet<string> = new Set(Object.keys(PERMISSIONS));

export function isKnownPermission(id: string): id is Permission {
  return KNOWN.has(id);
}

// Deny by default: an identifier the panel does not know grants nothing.
export function can(granted: readonly string[], permission: Permission): boolean {
  return isKnownPermission(permission) && granted.includes(permission);
}

export function hasAnyKnownPermission(granted: readonly string[]): boolean {
  return granted.some(isKnownPermission);
}

// The booleans a business slice receives as props; slices never see permissions themselves.
export function actionFlags<Flag extends string>(
  granted: readonly string[],
  required: Record<Flag, Permission>,
): Record<Flag, boolean> {
  const flags = {} as Record<Flag, boolean>;
  for (const flag of Object.keys(required) as Flag[]) {
    flags[flag] = can(granted, required[flag]);
  }
  return flags;
}
